import { BehaviorSubject, of } from 'rxjs';
import type { Observable } from 'rxjs';
import { catchError, distinctUntilChanged, map, switchMap, take } from 'rxjs/operators';
import { DEV_PERSIST_STORAGE_KEYS, IS_DEV_SESSION_PERSIST } from 'settings';

import apiService from './api';

export class TokenService {
  private token$: BehaviorSubject<string>;
  private refreshToken$: BehaviorSubject<string>;
  private accountsSsid$: BehaviorSubject<string>;
  private accountsId$: BehaviorSubject<string>;
  private qualificationKey$: BehaviorSubject<string>;

  constructor() {
    this.token$ = new BehaviorSubject(null);
    this.refreshToken$ = new BehaviorSubject(null);
    this.accountsSsid$ = new BehaviorSubject(null);
    this.accountsId$ = new BehaviorSubject(null);
    this.qualificationKey$ = new BehaviorSubject(null);

    if (IS_DEV_SESSION_PERSIST) {
      this.hydrateFromDevStorage();
    }
  }

  public getTokenSnapshot(): string | null {
    return this.token$.getValue();
  }

  public getAccountsSsidSnapshot(): string | null {
    return this.accountsSsid$.getValue();
  }

  public getToken(): Observable<string> {
    return this.token$.pipe(distinctUntilChanged());
  }

  public getRefreshToken(): Observable<string> {
    return this.refreshToken$.pipe(distinctUntilChanged());
  }

  public setToken(token: string): Observable<string> {
    if (token) {
      sessionStorage.setItem('authToken', token);

      if (IS_DEV_SESSION_PERSIST) {
        localStorage.setItem(DEV_PERSIST_STORAGE_KEYS.authToken, token);
      }
    } else {
      sessionStorage.removeItem('authToken');

      if (IS_DEV_SESSION_PERSIST) {
        localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.authToken);
      }
    }

    this.token$.next(token);
    return this.getToken();
  }

  public getAccountsSsid(): Observable<string> {
    return this.accountsSsid$.pipe(distinctUntilChanged());
  }

  public setAccountsSsid(ssid: string): Observable<string> {
    if (IS_DEV_SESSION_PERSIST) {
      if (ssid) {
        localStorage.setItem(DEV_PERSIST_STORAGE_KEYS.accountsSsid, ssid);
      } else {
        localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.accountsSsid);
      }
    }

    this.accountsSsid$.next(ssid);

    return this.getAccountsSsid();
  }

  public getAccountsId(): Observable<string> {
    return this.accountsId$.pipe(distinctUntilChanged());
  }

  public setAccountsId(id: string): Observable<string> {
    this.accountsId$.next(id);

    return this.getAccountsId();
  }

  public getQualificationKey(): Observable<string> {
    return this.qualificationKey$.pipe(distinctUntilChanged());
  }

  public setQualificationKey(id: string) {
    this.qualificationKey$.next(id);
  }

  public setRefreshToken(refreshToken: string): Observable<string> {
    if (IS_DEV_SESSION_PERSIST) {
      if (refreshToken) {
        localStorage.setItem(DEV_PERSIST_STORAGE_KEYS.refreshToken, refreshToken);
      } else {
        localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.refreshToken);
      }
    }

    this.refreshToken$.next(refreshToken);
    return this.getRefreshToken();
  }

  public clearToken(): Observable<void> {
    sessionStorage.removeItem('authToken');

    if (IS_DEV_SESSION_PERSIST) {
      localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.authToken);
      localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.refreshToken);
      localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.accountsSsid);
    }

    this.refreshToken$.next(null);
    return this.setToken(null).pipe(map(() => {}));
  }

  public decode<T>(token: string): T {
    try {
      const data = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      return currentTime > data.exp ? null : data;
    } catch (err) {
      return null;
    }
  }

  public tryRefreshToken() {
    return this.getRefreshToken().pipe(
      take(1),
      switchMap(refreshToken => apiService.post('/auth/refresh', { refreshToken })),
      switchMap(data => this.setToken(data.token)),
      map(() => true),
      catchError(() => of(false))
    );
  }

  /** Somente dev: relê JWT / refresh / ssid persistidos após F5. */
  private hydrateFromDevStorage(): void {
    try {
      const auth = localStorage.getItem(DEV_PERSIST_STORAGE_KEYS.authToken);

      if (auth) {
        if (this.decode(auth)) {
          this.token$.next(auth);
          sessionStorage.setItem('authToken', auth);
        } else {
          localStorage.removeItem(DEV_PERSIST_STORAGE_KEYS.authToken);
        }
      }

      const refresh = localStorage.getItem(DEV_PERSIST_STORAGE_KEYS.refreshToken);

      if (refresh) {
        this.refreshToken$.next(refresh);
      }

      const ssid = localStorage.getItem(DEV_PERSIST_STORAGE_KEYS.accountsSsid);

      if (ssid) {
        this.accountsSsid$.next(ssid);
      }
    } catch {
      // storage corrompido ou indisponível
    }
  }
}

const tokenService = new TokenService();
export default tokenService;
